import { Download } from 'lucide-react';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatDate } from '@/lib/utils/date.utils';
import { formatCents } from '@/lib/utils/format.utils';
import type { Invoice } from '@/lib/types/billing';
import { BILLING_STRINGS } from '../constants/billing-strings';

const COPY = BILLING_STRINGS.OVERVIEW;
const STATUS = BILLING_STRINGS.INVOICE_STATUS_LABELS;

export interface BillingHistoryTableProps {
  invoices: Invoice[];
}

/**
 * The native billing history (A-72, MYN-113.2).
 *
 * ## The table is ours, the PDF is Stripe's
 *
 * A-62 originally sent members to Stripe's portal for their invoices; A-72
 * amended it after the design showed an in-app history with per-row download.
 * So the rows render from local `Invoice` data and each download link points at
 * `invoicePdfUrl` - Stripe's own hosted document. Nothing here re-renders an
 * invoice we would then have to keep consistent with theirs.
 *
 * A row whose PDF is not ready yet shows why rather than a dead link: Stripe
 * finalises the document a moment after the charge, so a just-paid invoice can
 * legitimately have no URL for a few seconds.
 */
/** How each invoice status reads at a glance, resolved once. */
const STATUS_BADGE: Record<Invoice['status'], BadgeVariant> = {
  paid: 'success',
  open: 'warning',
  uncollectible: 'danger',
  draft: 'neutral',
  void: 'neutral',
};

const HEAD_CELL = 'px-4 py-3 text-xs font-medium tracking-wide uppercase';

/**
 * The period cell. Stripe stamps a one-time payment's invoice with the same
 * start and end, so a same-day range is shown as a one-time payment rather
 * than "Oct 21 - Oct 21" for a 7-day Issue Report (DAXE staging QA, 2026-10-21).
 */
function periodLabel({ periodStart, periodEnd }: Invoice): string {
  if (!periodStart || !periodEnd) return COPY.EMPTY_CELL;
  const start = formatDate(periodStart);
  const end = formatDate(periodEnd);
  return start === end ? COPY.ONE_TIME_PERIOD : `${start} - ${end}`;
}

export function BillingHistoryTable({ invoices }: BillingHistoryTableProps) {
  if (invoices.length === 0) {
    return <EmptyState title={COPY.HISTORY_EMPTY} />;
  }

  return (
    // Scrolls sideways on a phone rather than squeezing five columns.
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] text-left text-sm">
        <thead>
          <tr className="text-text-muted bg-surface-raised">
            <th scope="col" className={`${HEAD_CELL} rounded-l-lg`}>
              {COPY.COL_DATE}
            </th>
            <th scope="col" className={HEAD_CELL}>
              {COPY.COL_PERIOD}
            </th>
            <th scope="col" className={HEAD_CELL}>
              {COPY.COL_AMOUNT}
            </th>
            <th scope="col" className={HEAD_CELL}>
              {COPY.COL_STATUS}
            </th>
            <th scope="col" className={`${HEAD_CELL} rounded-r-lg`}>
              {COPY.COL_INVOICE}
            </th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr key={invoice.id} className="border-border-subtle border-b last:border-0">
              <td className="text-text-primary px-4 py-3.5">{formatDate(invoice.createdAt)}</td>
              <td className="text-text-secondary px-4 py-3.5">{periodLabel(invoice)}</td>
              <td className="text-text-primary px-4 py-3.5 font-semibold">
                {formatCents(invoice.amountDueCents)}
              </td>
              <td className="px-4 py-3.5">
                <Badge variant={STATUS_BADGE[invoice.status]} size="sm">
                  {STATUS[invoice.status]}
                </Badge>
              </td>
              <td className="px-4 py-3.5">
                {invoice.invoicePdfUrl ? (
                  <a
                    className="text-dkgreen inline-flex cursor-pointer items-center gap-1 font-medium hover:opacity-80"
                    href={invoice.invoicePdfUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {COPY.DOWNLOAD}
                    <Download aria-hidden="true" className="h-3.5 w-3.5" />
                  </a>
                ) : (
                  <span className="text-text-muted">{COPY.DOWNLOAD_PENDING}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
